import { API, axiosInstance, handleResponse, graphqlAxiosInstance } from './helper';
import { getRealmUserInfo } from './keycloak';
import { PractiseBasicDetails } from '../pages/DoctorManagement/types';
import { ROLES } from '../constants';

interface doctorStatsPaylaod {
  rangeType: string;
  organisationId?: string;
}

// Helper function to get organisationId from token for practice admins
const getOrganisationIdIfPracticeAdmin = () => {
  const userRole = localStorage.getItem('userRole') || '';

  if (userRole === ROLES.PRACTISE_ADMIN) {
    const token = localStorage.getItem('user_access_token') || '';
    if (token) {
      try {
        // Parse the JWT token to get the payload
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        // Return organisation ID from token if it exists
        if (tokenPayload['x-hasura-org-id']) {
          return tokenPayload['x-hasura-org-id'];
        }
      } catch (error) {
        console.error('Error parsing token:', error);
      }
    }
  }

  return null;
};

const getDoctorManagementDetails = () =>
  handleResponse(axiosInstance.get(API.getDoctorManagementDetails));

const getDoctorProfileCompletionData = (doctor_stats_paylaod: doctorStatsPaylaod) => {
  // Check if user is Practice Admin
  const organisationId = getOrganisationIdIfPracticeAdmin();

  // Add organisation ID if practice admin
  if (organisationId) {
    doctor_stats_paylaod.organisationId = organisationId;
  }

  return handleResponse(
    axiosInstance.post(API.getDoctorProfileCompletionDetails, doctor_stats_paylaod)
  );
};

const getDoctorRegistrationTrends = (doctor_stats_paylaod: doctorStatsPaylaod) => {
  // Check if user is Practice Admin
  const organisationId = getOrganisationIdIfPracticeAdmin();

  // Add organisation ID if practice admin
  if (organisationId) {
    doctor_stats_paylaod.organisationId = organisationId;
  }

  return handleResponse(axiosInstance.post(API.getDoctorRegistrationTrends, doctor_stats_paylaod));
};

export interface DoctorDetailPayload {
  practiseUserId: string;
  slotStartDate: string;
  slotEndDate: string;
}
const getDoctorProfileDetailsById = async (payload: DoctorDetailPayload) =>
  await handleResponse(
    axiosInstance.get(API.getDoctorProfileDetailsById, {
      params: payload
    })
  );

export interface SlotsPayload {
  practiseUserId: string;
  date: string;
}

const getSlotsByDateAndId = async (payload: SlotsPayload) =>
  await handleResponse(axiosInstance.get(API.getSlotsByDateAndId, { params: payload }));

const getAppointmentsByDateAndId = async (payload: SlotsPayload) =>
  await handleResponse(axiosInstance.get(API.getAppointmentsByDateAndId, { params: payload }));

export interface SlotsRangePayload {
  practiseUserId: string;
  rangeStartDate: string;
  rangeEndDate: string;
}

const getSlotsDatesByRange = async (payload: SlotsRangePayload) =>
  await handleResponse(axiosInstance.get(API.getSlotsDatesByRange, { params: payload }));

const getDoctorManagementStatCardsData = () => {
  // Check if user is Practice Admin
  const organisationId = getOrganisationIdIfPracticeAdmin();

  // If practice admin, pass organisationId as query parameter
  if (organisationId) {
    return handleResponse(
      axiosInstance.get(API.getDoctorManagementStatCardsData, {
        params: { organisationId }
      })
    );
  }

  // Otherwise use simple GET as before
  return handleResponse(axiosInstance.get(API.getDoctorManagementStatCardsData));
};

const fetchDoctorManagementTableData = async (limit = 10, updatedAt?: string) => {
  const query = `query get_doctor_management_table_details_v1($limit: Int, $updatedAt: timestamptz) @cached(ttl: 300) {
    practiseBasicDetails(order_by: {updated_at: desc}, where: {verificationStatus: {_eq: "verified"}, updated_at: {_lt: $updatedAt}}, limit: $limit) {
      practiseAdminId
      created_at
      updated_at
      gender
      verifiedOn
      practiseAdminLivePic {
        imageContent
      }
      userPractiseMappingRelation {
        contact
        role
        practiseProfileAppointmentRelation(where: {status: {_eq: "approved"}}, limit: 6, distinct_on: patientId) {
          patientUser {
            patientUserDocuments {
              imageContent
              patientId
            }
          }
        }
      }
    }
    practiseBasicDetails_aggregate(where: {verificationStatus: {_eq: "verified"}}) {
    aggregate {
      count(columns: id)
    }
  }
  }`;
  const variables = { limit, updatedAt: updatedAt || new Date().toISOString() };
  const response = await graphqlAxiosInstance.post(`/${API.hasuraGraphql}`, {
    query,
    variables
  });
  return response;
};

const getDoctorManagementList = async (limit = 10, updatedAt?: string) => {
  const response = await fetchDoctorManagementTableData(limit, updatedAt);
  const basicDetailsList = response?.data?.data?.practiseBasicDetails || [];
  const accessToken = localStorage.getItem('user_access_token') || '';

  const enrichedList = await Promise.all(
    basicDetailsList.map(async (doc: PractiseBasicDetails, index: number) => {
      let email = null;
      let firstName = null;
      let lastName = null;
      try {
        if (doc.practiseAdminId) {
          const keycloakData = await getRealmUserInfo(doc.practiseAdminId, accessToken);
          email = keycloakData.email;
          firstName = keycloakData.firstName;
          lastName = keycloakData.lastName;
        }
      } catch (error) {
        // fallback to nulls
      }
      return {
        id: doc.practiseAdminId,
        sNo: index + 1,
        fullName: firstName && lastName ? `${firstName} ${lastName}` : 'N/A',
        gender: doc.gender || '',
        verifiedOn: doc.verifiedOn,
        email: email || 'N/A',
        image: doc.practiseAdminLivePic?.imageContent || '',
        role: doc.userPractiseMappingRelation?.role || '',
        connectedUsers: doc.userPractiseMappingRelation?.practiseProfileAppointmentRelation || []
      };
    })
  );
  return {
    data: enrichedList,
    hasMore: enrichedList.length === limit,
    totalCount: response?.data?.data?.practiseBasicDetails_aggregate?.aggregate?.count || 0,
    lastUpdatedAt:
      basicDetailsList.length > 0 ? basicDetailsList[basicDetailsList.length - 1].verifiedOn : null
  };
};

export {
  getDoctorManagementDetails,
  getDoctorProfileCompletionData,
  getDoctorRegistrationTrends,
  getDoctorProfileDetailsById,
  getSlotsByDateAndId,
  getAppointmentsByDateAndId,
  getSlotsDatesByRange,
  getDoctorManagementStatCardsData,
  getDoctorManagementList
};
